import { useMemo } from 'react'
import BottomSheet from '../../design-system/patterns/BottomSheet.jsx'
import {
  PLANNER_DAY_LONG,
  groupStripDaysByWeek,
} from '../../utils/mealPlanHelpers.js'
import './planner.css'

function dayButtonState(day, meal, mealWeekStart = null) {
  if (!day?.planItemId || day.isPast) {
    return { kind: 'past', label: 'Past', disabled: true }
  }
  const isCurrent = Boolean(
    (meal?.id && day.meal?.id === meal.id)
    || (meal?.planItemId && String(meal.planItemId) === String(day.planItemId)),
  )
  if (isCurrent) {
    return { kind: 'current', label: 'Current', disabled: true }
  }
  if (day.meal) {
    const sameWeek = Boolean(mealWeekStart && day.weekStartDate === mealWeekStart)
    return {
      kind: 'occupied',
      label: meal?.assignedTo
        ? (sameWeek ? 'Swap' : 'Replace')
        : 'Replace',
      disabled: false,
      occupiedName: day.meal.name || 'Taken',
    }
  }
  return { kind: 'open', label: 'Open', disabled: false }
}

/**
 * Bottom sheet to assign or move a meal onto a day in this week or the next.
 */
export default function MobileMoveMealSheet({
  open,
  meal,
  stripDays = [],
  mealWeekStart = null,
  busy = false,
  anchorRect = null,
  onClose,
  onSelectDay,
}) {
  const groups = useMemo(() => groupStripDaysByWeek(stripDays), [stripDays])
  const isMove = Boolean(meal?.assignedTo)
  const title = meal
    ? (isMove ? `Move ${meal.name}` : `Add ${meal.name}`)
    : 'Pick a night'
  const hint = isMove
    ? 'Pick a night this week or next. Occupied nights ask before swapping or replacing.'
    : 'Pick a night this week or next. Occupied nights ask before replacing.'

  return (
    <BottomSheet
      open={open && Boolean(meal)}
      onClose={onClose}
      aria-label={title}
      className="mobile-move-meal-sheet"
      anchorRect={anchorRect}
      anchorPlacement="below"
    >
      <div className="mobile-move-meal-sheet-body">
        <div className="mobile-move-meal-sheet-header">
          <div className="eyebrow planner-eyebrow-xs">
            {isMove ? 'Move meal' : 'Add to plan'}
          </div>
          <div className="mobile-move-meal-sheet-title">{title}</div>
          <p className="mobile-move-meal-sheet-hint">{hint}</p>
        </div>

        {groups.length === 0 ? (
          <p className="mobile-move-meal-sheet-empty">No nights available right now.</p>
        ) : (
          <div className="mobile-move-meal-sheet-weeks">
            {groups.map((group) => (
              <section
                key={group.weekStartDate}
                className="mobile-move-meal-week"
                aria-label={group.label}
              >
                <div className="mobile-move-meal-week-label">{group.label}</div>
                <div className="mobile-move-meal-day-grid" role="group" aria-label={group.label}>
                  {group.days.map((day) => {
                    const state = dayButtonState(day, meal, mealWeekStart)
                    return (
                      <button
                        key={day.key}
                        type="button"
                        className={[
                          'mobile-move-meal-day-btn',
                          `is-${state.kind}`,
                          day.isToday ? 'is-today' : '',
                        ].filter(Boolean).join(' ')}
                        disabled={state.disabled || busy}
                        onClick={() => {
                          if (state.disabled || busy) return
                          onSelectDay?.(day)
                        }}
                        aria-label={
                          state.kind === 'current'
                            ? `${PLANNER_DAY_LONG[day.dayKey]} ${day.dateNum} — current night`
                            : state.kind === 'past'
                              ? `${PLANNER_DAY_LONG[day.dayKey]} ${day.dateNum} — past`
                              : state.kind === 'occupied'
                                ? `${PLANNER_DAY_LONG[day.dayKey]} ${day.dateNum} — ${state.label} with ${state.occupiedName}`
                                : `${PLANNER_DAY_LONG[day.dayKey]} ${day.dateNum} — open`
                        }
                      >
                        <span className="mobile-move-meal-day-letter" aria-hidden="true">
                          {PLANNER_DAY_LONG[day.dayKey]}
                        </span>
                        <span className="mobile-move-meal-day-date">{day.dateNum}</span>
                        <span className="mobile-move-meal-day-status">
                          {state.kind === 'occupied' ? state.occupiedName : state.label}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </BottomSheet>
  )
}
